import type { PermissionAction, PermissionGroup } from '@core/permissions/actions';

/**
 * Sidebar navigation as DATA rather than 130 lines of repeated template markup.
 *
 * Ported from `main-layout.component.html:24-130`. Each entry carries its own gate, so
 * `Sidebar.tsx` renders one loop instead of eight near-identical `<a mat-button>` blocks
 * each with its own `*ngIf`.
 *
 * NOT PORTED: the `/reports` nav item (main-layout.component.html:79-88) is commented
 * out in the Angular template and there is no `/reports` route, so it renders for nobody
 * today. Left out deliberately.
 */
export interface NavItem {
  id: string;
  label: string;
  icon: string;
  to: string;
  /** Query string appended to `to`, e.g. Business Partners' `?tab=work-queue`. */
  search?: string;
  /** Only match when the whole path+query matches (routerLinkActiveOptions.exact). */
  exact?: boolean;
  /** Hidden unless this action is permitted. */
  permission?: PermissionAction;
  /** Hidden unless this module group is visible. */
  module?: PermissionGroup;
  /** Visible to a delegatee regardless of `permission`/`module` — see `isNavItemVisible`. */
  orDelegatee?: boolean;
  /** Extra class on the icon — used for AI Search's amber tint. */
  iconClassName?: string;
}

export const NAV_ITEMS: NavItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'dashboard',
    to: '/dashboard',
  },
  // AI Search — hidden from the sidebar for now. Uncomment this block to bring it back; the
  // `/ai-search` route and page are unchanged.
  // {
  //   id: 'ai-search',
  //   label: 'AI Search',
  //   // The sparkle every AI feature in the app carries (AI Search hero, AI Doc Extract), not a robot.
  //   icon: 'auto_awesome',
  //   to: '/ai-search',
  //   permission: 'ai:search',
  //   iconClassName: 'vm-ai-icon',
  // },
  {
    id: 'business-partners',
    label: 'Business Partners',
    icon: 'groups',
    to: '/business-partners',
    search: '?tab=work-queue',
    exact: true,
  },
  // Above Review, by request.
  {
    id: 'create-bp',
    label: 'Create Business Partner',
    icon: 'person_add',
    to: '/vendor-details',
    permission: 'vendor:create',
  },
  /**
   * Partners returned for review — moved out of the Business Partners tab strip into its own entry,
   * by request. Ungated like Business Partners, whose list it used to be part of.
   */
  {
    id: 'review',
    label: 'Review',
    icon: 'rate_review',
    to: '/review',
  },
  /**
   * The approver's queue. NEW — added with the `/requests` screen ported from the CMD prototype.
   *
   * Gated on `vendor:approve`, matching its route guard, so a user who cannot act on a workflow is not
   * offered a queue of workflows to act on. The prototype shows this item only for its Supervisor
   * role; that permission is what the real model uses to say the same thing.
   */
  {
    /**
     * `assignment_turned_in`, NOT the prototype's bare checkmark: `fact_check` is already Approval
     * Workflow's icon two rows down, and two check-shaped glyphs in one rail are hard to tell apart.
     * This one is also in the classic Material Icons set the app self-hosts — `task_alt` is not.
     */
    id: 'requests',
    label: 'Requests',
    icon: 'assignment_turned_in',
    to: '/requests',
    permission: 'vendor:approve',
    /*
     * ⚠️ ALSO VISIBLE TO A DELEGATEE, who may hold no `vendor:approve` of their own. Standing in for an
     * approver is the entire point of a delegation, and this queue is where they would do it — hiding
     * the link would leave them a permission they cannot reach.
     */
    orDelegatee: true,
  },
  /**
   * ============================================================================
   * Delegation — TOP LEVEL, beside Requests rather than inside Settings.
   * ============================================================================
   *
   * Handing your approval authority to a colleague is part of doing approvals, not an administrative
   * setting — so it sits next to Requests, which is the other screen about approvals passing through you.
   *
   * ⚠️ UNGATED, matching its route. Raising a delegation is asking for something rather than granting it,
   * and the request is inert until an admin approves it. An ordinary approver holds no user-admin
   * permission and is exactly who needs this.
   */
  // Delegation — hidden from the sidebar for now. Uncomment to bring it back; the route and page are
  // unchanged.
  // {
  //   id: 'delegation',
  //   label: 'Delegation',
  //   icon: 'switch_account',
  //   to: '/delegation',
  // },
  /**
   * The admin queue for the above.
   *
   * Gated on `delegation_management`, matching its route's `del_request:view`. It was on
   * `user_management` only because no delegation permission existed; the API issues its own now.
   * `rule_folder` because `rule` is DOA Matrix's and `switch_account` is the row above.
   */
  // Delegation Approvals — hidden from the sidebar for now. Uncomment to bring it back; the route and
  // page are unchanged.
  // {
  //   id: 'delegation-approvals',
  //   label: 'Delegation Approvals',
  //   icon: 'rule_folder',
  //   to: '/delegation-approvals',
  //   module: 'delegation_management',
  // },
  /*
   * ============================================================================
   * Approval Workflow — COMMENTED OUT of the rail, by request
   * ============================================================================
   *
   * The screen and its route are untouched: `/approval-workflow` still resolves and is still gated on
   * the `vendor_approvals` group. Only the sidebar link is gone, so the page is reachable by URL or by a
   * direct link but is no longer offered.
   *
   * This mirrors what the Angular template did with `/reports` (see the note at the top of this file),
   * with one difference worth knowing: that item had no route at all, so it rendered for nobody. This one
   * works — it is simply unlisted.
   *
   * Restoring it is uncommenting this block. Nothing else changes; DOA Matrix below is a separate screen
   * with its own `matrix:view` gate and is unaffected.
   */
  // {
  //   id: 'approval-workflow',
  //   label: 'Approval Workflow',
  //   icon: 'fact_check',
  //   to: '/approval-workflow',
  //   module: 'vendor_approvals',
  // },
  /**
   * DOA by company code. NEW — added with the `/doa-matrix` screen ported from the CMD prototype's
   * `workflows.html`.
   *
   * Sits directly below Approval Workflow because the two are the same subject seen two ways: that one
   * is the global workflow DEFINITIONS, this one is how they route per COMPANY CODE.
   *
   * ⚠️ Gated on `matrix:view`, NOT on `vendor_approvals`. It shared the neighbour's gate only because no
   * matrix permission existed; the backend now issues one, and it must match the route's gate exactly —
   * a link that is offered and then bounces is worse than no link.
   *
   * `rule` for the icon: `account_tree` and `fact_check` are both taken by neighbours, and it is in the
   * classic Material Icons set the app self-hosts.
   */
  {
    id: 'doa-matrix',
    label: 'DOA Matrix',
    icon: 'rule',
    to: '/doa-matrix',
    permission: 'matrix:view',
  },
];

/**
 * Settings submenu.
 *
 * The Angular version switched an enum via `?tab=`, and only three of its five
 * sub-views were addressable. These are now real nested routes, so each entry is a
 * plain link and refresh/Back work on all of them. `/settings?tab=` still redirects
 * (see `router.tsx`) so existing bookmarks survive.
 */
export interface NavSubItem {
  id: string;
  label: string;
  icon: string;
  to: string;
  module?: PermissionGroup;
  /** Visible to a delegatee regardless of `permission`/`module` — see `isNavItemVisible`. */
  orDelegatee?: boolean;
  /**
   * An individual action gate, as `NavItem` has.
   *
   * Added for Data Management: the API models that screen with a single `bulk:upload` action rather
   * than a group, so a group-only gate could not express it. `Sidebar.isVisible` already reads both
   * fields for either shape.
   */
  permission?: PermissionAction;
}

export const SETTINGS_SUBITEMS: NavSubItem[] = [
  {
    id: 'manageUsers',
    label: 'Manage Users',
    icon: 'group',
    to: '/settings/users',
    module: 'user_management',
  },
  {
    /**
     * Gated on `bulk:upload`, the same action the route requires.
     *
     * It was ungated, faithfully preserving the Angular sidebar. Testing with a `role:view`-only
     * account showed why that cannot stand: the link was offered and clicking it bounced straight back
     * to the dashboard, because `/settings/data` requires `bulk:upload`. A link that always redirects
     * is worse than no link.
     */
    id: 'dataManagement',
    label: 'Data Management',
    icon: 'storage',
    to: '/settings/data',
    permission: 'bulk:upload',
  },
  {
    id: 'manageRoles',
    label: 'Manage Roles',
    icon: 'admin_panel_settings',
    to: '/settings/roles',
    module: 'role_management',
  },
];

/** Maps the legacy `?tab=` values onto the new nested routes for the redirect. */
export const LEGACY_SETTINGS_TABS: Record<string, string> = {
  manageUsers: '/settings/users',
  dataManagement: '/settings/data',
  manageRoles: '/settings/roles',
};

/**
 * Whether a nav entry is visible to a user holding `actions` / `groups`.
 *
 * ⚠️ EXTRACTED FROM `Sidebar` SO IT CAN BE TESTED. It lived inline in the component, which meant the
 * DENIED branch — the one that matters — was only exercised by logging in as a restricted user. With an
 * all-granted account a rule that accidentally always returns `true` looks perfectly correct.
 *
 * Both gates must pass, and an ABSENT permission is a denial, not a pass: `actions?.[x]` on a user whose
 * payload never mentioned `x` is `undefined`, which must hide the item rather than reveal it.
 */
export function isNavItemVisible(
  item: NavItem | NavSubItem,
  actions: Record<string, boolean> | null | undefined,
  groups: Record<string, { visible?: boolean }> | null | undefined,
  /**
   * Somebody has delegated their approval authority to this user.
   *
   * ⚠️ IT MUST MATCH THE ROUTE. `/requests` admits a delegatee (`orDelegatee` on its guard), so hiding
   * the link from them would leave the queue reachable only by typing the URL — the same
   * offered-but-bounced contradiction, inverted, that this function was extracted to prevent.
   *
   * Defaults to false so every existing caller and test keeps its behaviour.
   */
  isDelegatee = false,
): boolean {
  // The delegation overrides the whole gate, exactly as it does on the route.
  if (item.orDelegatee && isDelegatee) return true;
  if ('permission' in item && item.permission && !actions?.[item.permission]) return false;
  if (item.module && !(groups?.[item.module]?.visible ?? false)) return false;
  return true;
}
